import { useMemo } from 'react';
import { motion } from 'framer-motion';

const CONTINENT_CONFIG = {
  'Africa': { emoji: '🌍', color: 'amber' },
  'Asia': { emoji: '🌏', color: 'rose' },
  'Europe': { emoji: '🌍', color: 'blue' },
  'North America': { emoji: '🌎', color: 'emerald' },
  'South America': { emoji: '🌎', color: 'orange' },
  'Oceania': { emoji: '🌏', color: 'cyan' },
};

const ContinentPills = ({
  locations = [],
  activeContinent,
  onContinentSelect,
  className = ''
}) => {
  // Calculate counts per continent
  const continentCounts = useMemo(() => {
    const counts = {};
    locations.forEach(loc => {
      const continent = loc.continent;
      if (continent) {
        counts[continent] = (counts[continent] || 0) + 1;
      }
    });
    return counts;
  }, [locations]);

  // Get continents that have locations
  const availableContinents = useMemo(() => {
    return Object.keys(CONTINENT_CONFIG).filter(
      continent => continentCounts[continent] > 0
    );
  }, [continentCounts]);

  const handleClick = (continent) => {
    if (activeContinent === continent) {
      onContinentSelect?.(null); // Toggle off
    } else {
      onContinentSelect?.(continent);
    }
  };

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {/* All button */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => onContinentSelect?.(null)}
        className={`
          inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
          transition-all duration-200 border
          ${!activeContinent
            ? 'bg-gray-800 text-white border-gray-800 shadow-md'
            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          }
        `}
      >
        <span className="text-base">🌐</span>
        <span>All</span>
      </motion.button>

      {/* Continent pills */}
      {availableContinents.map((continent) => {
        const config = CONTINENT_CONFIG[continent];
        const count = continentCounts[continent] || 0;
        const isActive = activeContinent === continent;
        
        return (
          <motion.button
            key={continent}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleClick(continent)}
            className={`
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
              transition-all duration-200 border
              ${isActive
                ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }
            `}
          >
            <span className="text-base">{config.emoji}</span>
            <span className="hidden sm:inline">{continent}</span>
            <span className={`
              text-xs px-1.5 py-0.5 rounded-full
              ${isActive
                ? 'bg-blue-500 text-blue-100'
                : 'bg-gray-100 text-gray-500'
              }
            `}>
              {count}
            </span>
          </motion.button>
        );
      })}
    </div>
  );
};

export default ContinentPills;
